/**
 * Per-field + per-step validation.
 *
 * Returns a human-readable error string, or undefined when the value is
 * acceptable. The renderer surfaces these inline and blocks step advance
 * until the step's required fields validate.
 */
import type { Answers, Field } from "../types";

export type FieldValue = string | string[] | number | boolean | undefined;

function isEmpty(field: Field, value: FieldValue): boolean {
  if (field.type === "consent_checkbox") return value !== true;
  if (Array.isArray(value)) return value.length === 0;
  return value === undefined || value === null || value === "";
}

export function validateField(field: Field, value: FieldValue): string | undefined {
  if (isEmpty(field, value)) {
    if (!field.required) return undefined;
    return field.type === "consent_checkbox"
      ? "Please check this box to continue."
      : "This field is required.";
  }

  switch (field.type) {
    case "email":
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value))
        ? undefined
        : "Enter a valid email address.";
    case "tel": {
      const digits = String(value).replace(/\D/g, "");
      return digits.length >= 10 ? undefined : "Enter a valid phone number.";
    }
    case "zip":
      return /^\d{5}$/.test(String(value)) ? undefined : "Enter a 5-digit ZIP code.";
    case "number": {
      const n = Number(value);
      if (Number.isNaN(n)) return "Enter a number.";
      if (field.min !== undefined && n < field.min) return `Must be at least ${field.min}.`;
      if (field.max !== undefined && n > field.max) return `Must be at most ${field.max}.`;
      return undefined;
    }
    case "text": {
      const s = String(value);
      if (field.max_length && s.length > field.max_length) return "This entry is too long.";
      if (field.pattern && !new RegExp(field.pattern).test(s)) return "Please check this value.";
      return undefined;
    }
    case "textarea": {
      if (field.max_length && String(value).length > field.max_length) {
        return "This entry is too long.";
      }
      return undefined;
    }
    case "multi_select": {
      const arr = Array.isArray(value) ? value : [];
      if (field.min_select && arr.length < field.min_select) {
        return `Select at least ${field.min_select}.`;
      }
      if (field.max_select && arr.length > field.max_select) {
        return `Select at most ${field.max_select}.`;
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

/** Validate every field on a step. Returns errors keyed by field id. */
export function validateStep(fields: Field[], answers: Answers): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const err = validateField(field, answers[field.id] as FieldValue);
    if (err) errors[field.id] = err;
  }
  return errors;
}
